/**
 * Enterprise SEO Head Component — Direct DOM injection
 * Replaces react-helmet-async with useEffect + document manipulation.
 * Guarantees tags appear in live DOM regardless of provider context.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { 
  generateHreflangLinks, 
  generateCanonical, 
  generateBreadcrumbSchema,
  generateDynamicOGImage,
  getDefaultOGImage,
  normalizeSeoPath,
  SITE_URL
} from '@/lib/seo';
import { getSEOByRoute } from '@/lib/seo-descriptions';
import { useSeoI18nReady } from '@/lib/seo-i18n-loader';
import BRAND from '@/config/brand';
import {
  setMetaName,
  setMetaProperty,
  setCanonical,
  setHreflangLinks,
  setJsonLd,
  setOgLocaleAlternates,
  setHtmlAttrs,
} from '@/lib/seo-dom';
import { extractSeoRoutePath, isPrivateSeoRoute, isThinSeoPage } from '@/lib/seo-indexability';
import { sanitizeTitle } from '@/lib/seo-title';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  type?: 'website' | 'article' | 'product' | 'service';
  section?: string;
  prevPage?: string;
  nextPage?: string;
  hreflangOverrides?: Array<{ lang: string; href: string }>;
  canonicalOverride?: string;
  /** Opt-in (blog articles): use hreflangOverrides as-is instead of merging the full locale set. */
  hreflangExact?: boolean;
  /** Opt-in: language of the page content when it differs from the URL locale (WebPage.inLanguage). */
  contentLanguage?: string;
  /** Opt-in: emit SEOHead's JSON-LD as a single @graph script. */
  jsonLdGraph?: boolean;
  /** Opt-in: drop alternates for languages whose version of this page is not natively translated (noindex). */
  hreflangExcludeLangs?: string[];
  /** Opt-in (404 pages): emit no canonical and no og:url, so an unknown URL never claims to be a canonical page. */
  noCanonical?: boolean;
}

export function SEOHead({ 
  title, 
  description, 
  image,
  imageWidth = 1200,
  imageHeight = 630,
  imageAlt,
  noIndex = false,
  jsonLd,
  keywords = [],
  author = BRAND.name,
  publishedTime,
  modifiedTime,
  type = 'website',
  section,
  prevPage,
  nextPage,
  hreflangOverrides,
  canonicalOverride,
  hreflangExact = false,
  contentLanguage,
  jsonLdGraph = false,
  hreflangExcludeLangs,
  noCanonical = false,
}: SEOHeadProps) {
  const { language } = useLanguage();
  const location = useLocation();
  
  const siteName = BRAND.name;
  const twitterHandle = '@bpo-contact';
  const themeColor = '#0B1628';
  
  useSeoI18nReady(); // re-render once translated catalog arrives
  const catalogSEO = getSEOByRoute(location.pathname, language);
  
  const rawTitle = title
    ? (title.includes('|') ? title : `${title} | ${siteName}`)
    : catalogSEO.title;

  // Template-level guard: titles are price-free and <= 60 chars. The trim happens at
  // the END of the string (word boundary), so the descriptive part survives instead of
  // collapsing to the brand name alone.
  const pageTitle = sanitizeTitle(rawTitle);

  // Template-level guard: descriptions under 50 chars are effectively empty.
  // Extend with the sitewide description rather than inventing page-specific claims.
  const ensureDescription = (value: string | undefined, min = 50, max = 160): string => {
    const fallback = BRAND.seo?.description || `${siteName} — multilingual BPO, contact center and software development outsourcing.`;
    let out = (value || '').trim();
    // CJK scripts are denser: a native 15+ character description is complete, and
    // appending the English sitewide sentence would produce mixed-language snippets.
    const isCJK = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/.test(out);
    if (isCJK && out.length >= 15) return out.length > max ? out.slice(0, max) : out;
    if (out.length < min) {
      out = out ? `${out.replace(/[.\s]+$/, '')}. ${fallback}` : fallback;
    }
    if (out.length > max) {
      const cut = out.slice(0, max);
      const lastSpace = cut.lastIndexOf(' ');
      out = (lastSpace > min ? cut.slice(0, lastSpace) : cut).replace(/[\s,;–—-]+$/, '');
    }
    return out;
  };

  const pageDescription = ensureDescription(description || catalogSEO.description);

  
  
  const normalizeSeoUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      parsed.pathname = normalizeSeoPath(parsed.pathname);
      return parsed.toString();
    } catch {
      return normalizeSeoPath(url);
    }
  };

  const normalizeSeoJsonLd = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map(normalizeSeoJsonLd);
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, nestedValue]) => [key, normalizeSeoJsonLd(nestedValue)])
      );
    }

    if (typeof value === 'string') {
      if (value.startsWith(SITE_URL)) {
        return normalizeSeoUrl(value);
      }

      return normalizeSeoPath(value);
    }

    return value;
  };

  const buildSafeHreflangLinks = (pathname: string, links: Array<{ lang: string; href: string }>) => {
    const cleanedPath = normalizeSeoPath(pathname);
    const segments = cleanedPath.split('/').filter(Boolean);
    const langSegment = segments[0];

    if (!langSegment || !/^[a-z]{2}$/i.test(langSegment)) {
      return links;
    }

    const rest = segments.slice(1);
    const stableTopLevelRoutes = new Set([
      'about', 'contact', 'start', 'faq', 'pricing', 'services', 'careers', 'freelancers', 'jobs', 'blog', 'quote',
      'how-it-works', 'regions', 'clients', 'partners', 'legal', 'security', 'compliance', 'trust-center',
      'ai-contact-center', 'why-us', 'leadership', 'testimonials', 'case-studies', 'resources', 'guides',
      'glossary', 'newsroom', 'recognitions', 'fact-sheet', 'onboarding', 'compare', 'solutions', 'industries', 'marketplace', 'nos-services'
    ]);
    const stableNestedNamespaces = new Set(['services', 'careers', 'blog', 'legal', 'regions', 'bpo-hub', 'industries', 'markets', 'freelancers', 'marketplace', 'nos-services']);

    // Programmatic SEO prefixes — these pages exist in all languages
    const programmaticPrefixes = ['bpo-services-', 'it-development-', 'web-development-', 'digital-marketing-', 'call-center-', 'software-development-agency-'];
    const isProgrammaticSeoPage = rest.length === 1 && programmaticPrefixes.some(p => rest[0].startsWith(p));

    const shouldMirrorAcrossLocales =
      rest.length === 0 ||
      (rest.length === 1 && stableTopLevelRoutes.has(rest[0])) ||
      (rest.length >= 2 && stableNestedNamespaces.has(rest[0])) ||
      isProgrammaticSeoPage;

    if (shouldMirrorAcrossLocales) {
      return links;
    }

    const selfHref = `${SITE_URL}${cleanedPath}`;
    return [
      { lang: langSegment.toLowerCase(), href: selfHref },
      { lang: 'x-default', href: selfHref }
    ];
  };

  const canonical = canonicalOverride || generateCanonical(location.pathname);
  const thematicSubtitle = (() => {
    const path = location.pathname.replace(/^\/[a-z]{2}\/?/, '/');
    if (/^\/(services\/(call-center|bpo|customer)|call-center|bpo)/.test(path)) return 'Multilingual BPO & Call Center Outsourcing';
    if (/^\/(services\/(web|it|digital|software)|it-development|web-development)/.test(path)) return 'Custom Software, SaaS & Mobile Development';
    if (/^\/(services\/(ai|voice-ai|chatbot)|ai-contact-center)/.test(path)) return 'AI Contact Center & Automation';
    if (/^\/(careers|jobs)/.test(path)) return 'Careers, Recruitment & Multilingual Talent';
    if (/^\/(marketplace|freelancers?)/.test(path)) return 'Freelance Talent Marketplace';
    if (/^\/(pricing|quote|roi)/.test(path)) return 'Pricing, ROI & Commercial Plans';
    if (/^\/(trust-center|security|compliance|legal)/.test(path)) return 'Security, Compliance & Trust';
    if (/^\/(case-studies|testimonials)/.test(path)) return 'Proof, Outcomes & Client Success';
    if (/^\/(blog|insights|guides|glossary)/.test(path)) return 'Insights, Guides & Industry Expertise';
    return 'Global BPO & Digital Factory';
  })();
  const ogImage = image || generateDynamicOGImage({ title: pageTitle, subtitle: thematicSubtitle, lang: language, path: location.pathname }) || getDefaultOGImage();
  const ogImageAlt = imageAlt || `${pageTitle} — ${thematicSubtitle}`;
  // MERGE hreflangOverrides with full 29-lang set for bidirectionality
  const baseHreflangLinks = generateHreflangLinks(location.pathname, language);
  const mergedHreflangLinks = (() => {
    if (!hreflangOverrides) return baseHreflangLinks;
    if (hreflangExact) return hreflangOverrides;
    // Build a map: overrides take priority, then fill missing langs from default
    const overrideMap = new Map(hreflangOverrides.map(l => [l.lang, l.href]));
    const merged: Array<{ lang: string; href: string }> = [];
    for (const link of baseHreflangLinks) {
      merged.push({
        lang: link.lang,
        href: overrideMap.get(link.lang) || link.href,
      });
      overrideMap.delete(link.lang);
    }
    // Add any extra overrides not in the default set
    for (const [lang, href] of overrideMap) {
      merged.push({ lang, href });
    }
    return merged;
  })();
  // An exact, page-declared hreflang set is authoritative: never collapse it to self.
  const hreflangLinksAll = (hreflangExact && hreflangOverrides ? mergedHreflangLinks : buildSafeHreflangLinks(location.pathname, mergedHreflangLinks)).map(link => ({
    ...link,
    href: normalizeSeoUrl(link.href),
  }));
  const hreflangLinks = hreflangExcludeLangs?.length
    ? hreflangLinksAll.filter(l => !hreflangExcludeLangs.includes(l.lang.split('-')[0]))
    : hreflangLinksAll;
  
  // Thin-page detection is done inside useEffect to avoid closure issues

  const ogLocaleMap: Record<string, string> = {
    en: 'en_US', fr: 'fr_FR', es: 'es_ES', ar: 'ar_MA', de: 'de_DE',
    it: 'it_IT', pt: 'pt_PT', nl: 'nl_NL', pl: 'pl_PL', ru: 'ru_RU',
    zh: 'zh_CN', ja: 'ja_JP', tr: 'tr_TR', ko: 'ko_KR', sv: 'sv_SE', da: 'da_DK',
    ro: 'ro_RO', cs: 'cs_CZ', hu: 'hu_HU', fi: 'fi_FI', hi: 'hi_IN',
    th: 'th_TH', vi: 'vi_VN', el: 'el_GR', no: 'nb_NO', id: 'id_ID',
    ms: 'ms_MY', tl: 'tl_PH', bn: 'bn_BD',
  };

  // ── Direct DOM injection ──
  useEffect(() => {
    const currentHostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    const isCanonicalHost = currentHostname === 'bpo-contact.com' || currentHostname === 'www.bpo-contact.com';
    const isPreviewOrLovableDomain = currentHostname.endsWith('.lovableproject.com') || (currentHostname.endsWith('.lovable.app') && !isCanonicalHost);

    // Thin-page detection — done INSIDE useEffect to prevent stale closures
    const routePath = extractSeoRoutePath(location.pathname);
    const isThinPage = isThinSeoPage(routePath);
    const isPrivateRoute = isPrivateSeoRoute(routePath);
    const pageSignals = (() => {
      if (/^(services\/(call-center|bpo|customer)|call-center|bpo)/.test(routePath)) {
        return {
          subject: 'Call Center Outsourcing, BPO, Multilingual Customer Support, Technical Support Outsourcing',
          classification: 'Business Process Outsourcing',
          category: 'BPO Services',
          coverage: 'Europe, North America, Middle East, Africa',
          pageTopic: 'Global multilingual call center outsourcing',
        };
      }
      if (/^(services\/(web|it|digital|software)|it-development|web-development|digital-marketing|seo-audit)/.test(routePath)) {
        return {
          subject: 'Software Development, Web Development, Mobile Apps, SaaS Development, AI Automation',
          classification: 'Software Development Services',
          category: 'Digital Factory',
          coverage: 'Worldwide',
          pageTopic: 'Global software development and digital delivery',
        };
      }
      if (/^(services\/(ai|voice-ai|chatbot)|ai-contact-center)/.test(routePath)) {
        return {
          subject: 'AI Contact Center, Voice AI, Chatbots, Automation, Agent Assist',
          classification: 'AI Automation Services',
          category: 'AI Contact Center',
          coverage: 'Worldwide',
          pageTopic: 'AI-powered customer operations and automation',
        };
      }
      if (/^(careers|jobs)/.test(routePath)) {
        return {
          subject: 'Careers, Recruitment, Call Center Jobs, Software Developer Jobs, QA Jobs',
          classification: 'Recruitment',
          category: 'Careers',
          coverage: 'Morocco, Spain, UAE, Remote',
          pageTopic: 'Recruitment and multilingual hiring',
        };
      }
      if (/^(marketplace|freelancers?)/.test(routePath)) {
        return {
          subject: 'Freelance Talent, Developers, Designers, QA, Remote Specialists',
          classification: 'Marketplace',
          category: 'Freelancer',
          coverage: 'Worldwide',
          pageTopic: 'Freelance talent acquisition and project staffing',
        };
      }
      if (/^(trust-center|security|compliance|legal)/.test(routePath)) {
        return {
          subject: 'Security, Compliance, GDPR, Trust & Safety',
          classification: 'Trust Center',
          category: 'Security',
          coverage: 'Worldwide',
          pageTopic: 'Security, privacy and compliance assurances',
        };
      }
      return {
        subject: 'BPO, Call Center Outsourcing, Software Development, AI Automation',
        classification: 'Business Services',
        category: 'BPO & Digital Factory',
        coverage: 'Worldwide',
        pageTopic: 'Global BPO and software development',
      };
    })();
    // Prevent parameterized URLs from being indexed (e.g. ?page=2, ?filter=xyz)
    const hasQueryParams = location.search.length > 0;
    // SHELL-FALLBACK-1910: the initial URL had no prerendered HTML → keep it noindex.
    const isShellFallback = typeof window !== 'undefined'
      && (window as unknown as { __BPO_SHELL_FALLBACK_PATH__?: string }).__BPO_SHELL_FALLBACK_PATH__ === location.pathname;
    const robotsContent = (noIndex || isShellFallback || isThinPage || isPrivateRoute || hasQueryParams || isPreviewOrLovableDomain)
      ? (isPreviewOrLovableDomain || isPrivateRoute ? 'noindex,nofollow' : 'noindex,follow')
      : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

    // thin-page check: routePath, isThinPage, robotsContent — debug removed for prod

    // HTML attributes
    setHtmlAttrs(language, language === 'ar' ? 'rtl' : 'ltr');

    // Title
    document.title = pageTitle;

    // Primary meta tags
    setMetaName('title', pageTitle);
    setMetaName('description', pageDescription);
    // keywords meta tag removed — ignored by search engines since 2009
    setMetaName('author', author);
    setMetaName('robots', robotsContent);
    setMetaName('googlebot', robotsContent);
    setMetaName('bingbot', robotsContent);
    setMetaName('yandexbot', robotsContent);
    setMetaName('slurp', robotsContent);
    setMetaName('naverbot', robotsContent);
    setMetaName('baiduspider', robotsContent);
    setMetaName('seznambot', robotsContent);

    // Trailing slash normalization — redirect if present (clean URL policy)
    if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
      const cleanPath = location.pathname.replace(/\/+$/, '');
      window.history.replaceState(null, '', cleanPath + location.search + location.hash);
    }

    // Canonical — always set, strip trailing slash and query params
    setCanonical(canonical);

    // Hreflang — skip for thin/noindex/query-param pages to avoid pointing crawlers to noindex URLs
    // NOTE: preview/lovable hosts DO get hreflang. They stay noindex,nofollow (see
    // robotsContent above), so alternates cost nothing there, and emitting them keeps
    // the preview verifiable against production instead of silently rendering 0.
    if (!isThinPage && !isPrivateRoute && !noIndex && !hasQueryParams) {
      setHreflangLinks(hreflangLinks);
    } else {
      setHreflangLinks([]); // Clear any existing hreflang
    }

    // Theme, app & topical metadata
    setMetaName('theme-color', themeColor);
    setMetaName('application-name', siteName);
    setMetaName('apple-mobile-web-app-title', siteName);
    setMetaName('apple-mobile-web-app-capable', 'yes');
    setMetaName('apple-mobile-web-app-status-bar-style', 'black-translucent');
    setMetaName('mobile-web-app-capable', 'yes');
    setMetaName('format-detection', 'telephone=no');
    setMetaName('subject', pageSignals.subject);
    setMetaName('classification', pageSignals.classification);
    setMetaName('category', pageSignals.category);
    setMetaName('coverage', pageSignals.coverage);
    setMetaName('distribution', 'global');
    setMetaName('rating', 'general');
    setMetaName('target', 'all');
    setMetaName('abstract', pageDescription);
    setMetaName('topic', pageSignals.pageTopic);

    // Open Graph
    setMetaProperty('og:type', type);
    setMetaProperty('og:title', pageTitle);
    setMetaProperty('og:description', pageDescription);
    setMetaProperty('og:url', normalizeSeoUrl(canonical));
    if (noCanonical) {
      document.querySelectorAll('link[rel="canonical"], meta[property="og:url"]').forEach((el) => el.remove());
    }
    setMetaProperty('og:site_name', siteName);
    setMetaProperty('og:image', ogImage);
    setMetaProperty('image:secure_url', ogImage);
    setMetaProperty('image:width', String(imageWidth));
    setMetaProperty('image:height', String(imageHeight));
    setMetaProperty('image:alt', ogImageAlt);
    setMetaProperty('image:type', ogImage.includes('/api/og') || ogImage.endsWith('.svg') ? 'image/svg+xml' : ogImage.endsWith('.webp') ? 'image/webp' : ogImage.endsWith('.jpg') || ogImage.endsWith('.jpeg') ? 'image/jpeg' : 'image/png');
    setMetaProperty('og:locale', ogLocaleMap[language] || 'en_US');

    // OG locale alternates
    const altLocales = Object.entries(ogLocaleMap)
      .filter(([lang]) => lang !== language)
      .map(([, locale]) => locale);
    setOgLocaleAlternates(altLocales);

    // Article-specific
    if (publishedTime) setMetaProperty('article:published_time', publishedTime);
    if (modifiedTime) setMetaProperty('article:modified_time', modifiedTime);
    if (type === 'article') setMetaProperty('article:author', author);
    if (type === 'article' && section) setMetaProperty('article:section', section);

    // Twitter Card
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:site', twitterHandle);
    setMetaName('twitter:creator', twitterHandle);
    setMetaName('twitter:title', pageTitle);
    setMetaName('twitter:description', pageDescription);
    setMetaName('twitter:image', ogImage);
    setMetaName('image:src', ogImage);
    setMetaName('image:alt', ogImageAlt);

    // LinkedIn
    setMetaProperty('linkedin:owner', 'bpo-contact');

    // Geo
    setMetaName('geo.region', 'MA-06');
    setMetaName('geo.placename', 'Casablanca');
    setMetaName('geo.position', '33.5731;-7.5898');
    setMetaName('ICBM', '33.5731, -7.5898');

    // JSON-LD schemas
    const autoBreadcrumbs = (() => {
      const segments = location.pathname.replace(/\/$/, '').split('/').filter(Boolean);
      if (segments.length <= 1) return null;
      const langSegment = segments[0];
      const crumbs = [{ name: 'Home', href: `/${langSegment}` }];
      let currentPath = `/${langSegment}`;
      for (let i = 1; i < segments.length; i++) {
        currentPath += `/${segments[i]}`;
        const label = segments[i].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        crumbs.push({ name: label, href: currentPath });
      }
      return generateBreadcrumbSchema(crumbs);
    })();

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: pageTitle,
      description: pageDescription,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      mainEntity: { '@id': `${SITE_URL}/#organization` },
      datePublished: publishedTime || '2024-01-15',
      dateModified: modifiedTime || '2026-03-21',
      inLanguage: contentLanguage || language,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.hero-description', '.faq-answer', '.page-summary', '[data-speakable]'],
      },
      ...(type === 'article' ? { author: { '@type': 'Organization', name: siteName } } : {}),
    };

    const schemas = [
      webPageSchema,
      ...(autoBreadcrumbs ? [autoBreadcrumbs] : []),
      ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [])
    ].map(schema => normalizeSeoJsonLd(schema)) as Record<string, unknown>[];

    if (jsonLdGraph) {
      const graph = schemas.map(({ ['@context']: _ctx, ...rest }) => rest);
      setJsonLd([{ '@context': 'https://schema.org', '@graph': graph }]);
    } else {
      setJsonLd(schemas);
    }

    // Pagination
    if (prevPage || nextPage) {
      const paginationLinks: Array<{ rel: string; href: string }> = [];
      if (prevPage) paginationLinks.push({ rel: 'prev', href: prevPage });
      if (nextPage) paginationLinks.push({ rel: 'next', href: nextPage });
      // Simple approach: create/update
      for (const { rel, href } of paginationLinks) {
        let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
        if (!el) {
          el = document.createElement('link');
          el.rel = rel;
          document.head.appendChild(el);
        }
        el.href = href;
      }
    }

    // DOM injected — debug removed for prod
  }, [language, location.pathname, location.search, noIndex, pageTitle, pageDescription, canonical, ogImage, type, publishedTime, modifiedTime, jsonLd, hreflangLinks, contentLanguage, jsonLdGraph, noCanonical]);

  // This component renders nothing — all work is done via useEffect
  return null;
}
